/**
 * Shared switch table column definitions and utilities.
 *
 * Used by both PortfolioTradeV2/SwitchSection (one-time switch) and SwitchPlansTrade (recurring switch plans).
 * Individual consumers configure behavior through `options` props — no duplication of column logic.
 */
import React from "react";
import _ from "lodash";

import {
  BrokerFeeColumn,
  DistributeButton,
  getItemBrokerFeeData,
} from "../../../TradeStep/components/PortfolioTrade/table-data";
import { CurrencyNumberFormat } from "../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import { PercentageNumberFormat } from "../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import { PrimaryButton } from "../../../../../../components/Buttons";
import { DeleteActionColumn, DiscountColumn, NameCol, SRRICol, SustainabilityCol } from "../PortfolioTradeV2/table-data";
import SwitchQuantityField from "../PortfolioTradeV2/components/SwitchSection/SwitchQuantityField";
import { getAssetInternalId } from "../../../../../CustomerDashboard/utils";
import { getNumberValue, totalTransactionAmount } from "../../../../utils";
import { withEuroOrDash } from "../../../../../../utils/utils";

// region ROW TYPE HELPERS

export const SWITCH_ROW_TYPE = {
  SWITCH_OUT: 1,
  SWITCH_IN: 2,
  SWITCH_IN_HEADER: 3,
};

export const isSwitchOut = (item) => item.rowType === SWITCH_ROW_TYPE.SWITCH_OUT;
export const isSwitchInHeader = (item) => item.rowType === SWITCH_ROW_TYPE.SWITCH_IN_HEADER;
export const isSwitchIn = (item) => item.rowType === SWITCH_ROW_TYPE.SWITCH_IN;

// endregion

// region COLUMNS
export const SwitchInIcon = ({marginTop=0}) => (
  <i
    className={"fa fa-level-up fa-rotate-90"}
    style={{ fontSize: 18, color: "#838A93", marginTop }}
  />
)
/**
 * Position column — shows row number for switch-out, arrow icon for switch-in rows.
 */
export const SwitchPositionCol = {
  header: [() => "Nr."],
  body: [
    ({ item }) => {
      if (isSwitchOut(item)) {
        return `${item.position}.`;
      }
      return <SwitchInIcon maringTop={isSwitchInHeader(item) ? 2 : 0} />
    },
  ],
};

/**
 * Name column factory.
 *
 * @param {object} config
 * @param {function} config.onAddClick - (item, options) => void — called when add button is clicked
 * @param {number} [config.footerColSpan=3] - colspan for footer cell
 */
export const createSwitchNameCol = ({ onAddClick, footerColSpan = 3 }) => ({
  ...NameCol,
  body: [
    ({ item, options }) => {
      if (isSwitchInHeader(item)) {
        return (
          <PrimaryButton
            text={"Zielinvestment hinzufügen"}
            variant={"outlined"}
            icon={<i className={"fal fa-plus"} />}
            onButtonClick={() => onAddClick(item, options)}
            style={{ marginTop: -4, height: 30 }}
          />
        );
      }

      return NameCol.body.map((Renderer, idx) => (
        <Renderer item={item} options={options} key={idx} />
      ));
    },
  ],
  colSpan: { footer: footerColSpan },
});

/**
 * Sustainability column — hidden for switch-in-header rows.
 */
export const SwitchSustainabilityCol = {
  ...SustainabilityCol,
  body: [
    ({ item, options }) => {
      if (isSwitchInHeader(item)) {
        return null;
      }
      return SustainabilityCol.body.map((Renderer, idx) => (
        <Renderer key={idx} item={item} options={options} />
      ));
    },
  ],
};

/**
 * SRRI column — shows "Saldo" label for switch-in-header rows.
 */
export const SwitchSRRICol = {
  header: SRRICol.header,
  body: [
    ({ item, options }) => {
      if (isSwitchInHeader(item)) {
        return (
          <div style={{ textAlign: "right" }}>
            <b>Saldo</b>
          </div>
        );
      }
      return SRRICol.body.map((Renderer, idx) => (
        <Renderer key={idx} item={item} options={options} />
      ));
    },
  ],
};

// region DEFAULT SWITCH-IN RENDERERS

const isInputDisabled = (item) => item.sell_all || item.action === "delete";

// endregion

/**
 * Shared distribute button renderer for switch-in-header rows.
 * Used by both one-time switch and switch plans to show the "Verteilen" button with saldo state.
 */
export const switchInHeaderDistribute = (item, options, switchOutItem) => {
  const headerOptions = {
    ...options,
    transactionSaldo: options.getTransactionSaldo(switchOutItem),
    onDistributeQuantitiesClick: () => options.handleDistributeQuantitiesClick(switchOutItem)(),
  };

  return (
    <DistributeButton
      classes={{}}
      options={headerOptions}
      variant={"text"}
      icon={<i className={"fa fa-random"} />}
      style={{ height: 30 }}
    />
  );
};

/**
 * Euro amount column factory.
 *
 * @param {object} config
 * @param {function} config.switchOut - (item, options) => ReactNode — content for switch-out rows
 * @param {function} config.switchInHeader - (item, options, switchOutItem) => ReactNode — content for header rows
 * @param {String} config.switchInErrorField -
 */
export const createSwitchAmountEuroCol = ({ switchOut, switchInHeader, switchInErrorField='transaction_value_euro'  }) => ({
  header: [() => <>Betrag<br />Tausch</>],
  body: [
    ({ item, options }) => {
      if (isSwitchOut(item)) {
        return switchOut(item, options);
      }

      const switchOutItem = options.switchOutsMap[item.switchOutItemId];

      if (isSwitchInHeader(item)) {
        return switchInHeader(item, options, switchOutItem);
      }

      // SWITCH_IN
      return (
        <SwitchQuantityField
          value={getNumberValue(_.get(item, "calculated.transaction_value_euro"))}
          onChange={(value) => options.handleSwitchAmountEuroChanged(item, value, switchOutItem)}
          error={_.get(item.errors, switchInErrorField)}
          inputComponent={CurrencyNumberFormat}
          inputProps={{ allowNegative: false, fixedDecimalScale: true }}
          disabled={isInputDisabled(item)}
        />
      )
    },
  ],
  footer: [
    ({ items }) => {
      const total = totalTransactionAmount(items.filter(isSwitchOut));
      return <b>{withEuroOrDash(total)}</b>;
    },
  ],
  cellClassName: "switch-amount-cell with-input",
  align: "right",
});

/**
 * Percentage column factory.
 *
 * @param {object} config
 * @param {function} config.switchOut - (item, options) => ReactNode — content for switch-out rows
 * @param {function} [config.switchInHeader] - (item, options, switchOutItem) => ReactNode — content for header rows (default: null)
 */
export const createSwitchPercentageCol = ({ switchOut, switchInHeader, header }) => ({
  header: [header || (() => <>Prozent<br />Tausch</>)],
  body: [
    ({ item, options }) => {
      if (isSwitchOut(item)) {
        return switchOut(item, options);
      }

      if (isSwitchInHeader(item)) {
        return switchInHeader ? switchInHeader(item, options, options.switchOutsMap[item.switchOutItemId]) : null;
      }

      // SWITCH_IN
      const switchOutItem = options.switchOutsMap[item.switchOutItemId];
      return (
        <SwitchQuantityField
          value={getNumberValue(_.get(item, "calculated.transaction_value_percentage"))}
          onChange={(value) => options.handleSwitchPercentageChanged(item, value, switchOutItem)}
          error={_.get(item.errors, "transaction_value_percentage")}
          inputComponent={PercentageNumberFormat}
          inputProps={{ allowNegative: false }}
          disabled={isInputDisabled(item)}
        />
      );
    },
  ],
  footer: [() => null],
  cellClassName: "switch-percentage-cell with-input",
  align: "right",
});

/**
 * Broker fee column — only shows for switch-in rows, calculates total in footer.
 */
export const SwitchBrokerFeeCol = {
  ...BrokerFeeColumn,
  body: [
    ({ item, options }) => {
      if (isSwitchIn(item)) {
        const Renderer = BrokerFeeColumn.body[0];
        const switchOutItem = options.switchOutsMap[item.switchOutItemId];
        const inOptions = { ...options, switchOutItem };
        return <Renderer item={item} options={inOptions} />;
      }
      return null;
    },
  ],
  footer: [
    ({ items, options }) => {
      const total = (items || []).reduce((sum, item) => {
        let brokerFee = 0;
        if (isSwitchIn(item)) {
          const switchOutItem = options.switchOutsMap[item.switchOutItemId];
          brokerFee = getItemBrokerFeeData(item, { ...options, switchOutItem }).netFeeEur;
        }
        return sum + brokerFee;
      }, 0);

      return <b>{withEuroOrDash(total)}</b>;
    },
  ],
};

/**
 * Discount column — only shows for switch-in rows.
 */
export const SwitchDiscountCol = {
  ...DiscountColumn,
  body: [
    ({ item, options }) => {
      if (isSwitchIn(item)) {
        const Renderer = DiscountColumn.body[0];
        return <Renderer item={item} options={options} />;
      }
      return null;
    },
  ],
};

/**
 * Actions (delete) column — hidden for switch-in-header rows.
 */
export const SwitchActionsCol = {
  ...DeleteActionColumn,
  body: [
    ({ item, options }) => {
      if (isSwitchInHeader(item)) {
        return null;
      }
      const Renderer = DeleteActionColumn.body[0];
      return <Renderer item={item} options={options} />;
    },
  ],
};

// endregion

// region DATA SOURCE BUILDER

/**
 * Builds a flat dataSource array for a unified switch table.
 *
 * Each switch transaction produces:
 *   1. SWITCH_OUT row (the source instrument)
 *   2. SWITCH_IN_HEADER row (technical row with add button / saldo) — skipped when hideSwitchIns=true
 *   3. N × SWITCH_IN rows (the target instruments) — skipped when hideSwitchIns=true
 *
 * Works for both one-time switches and switch plans.
 *
 * @param {Array} switchTransactions - Array of switch-out items, each containing a `buy` array
 * @param {object} [options]
 * @param {boolean} [options.hideSwitchIns=false] - If true, only switch-out rows are included (e.g. for delete action)
 * @param {function} [options.getItemId] - (item) => string — unique ID resolver for switch-out items. Defaults to getAssetInternalId(item.data). Use payment_id-based resolver for edit/delete plans.
 * @returns {Array} Flat array suitable for DashboardTable dataSource
 */
export const buildUnifiedSwitchDataSource = (switchTransactions, { hideSwitchIns = false, getItemId } = {}) => {
  const rows = [];

  (switchTransactions || []).forEach((switchOutItem, groupIndex) => {
    if (!switchOutItem || !switchOutItem.data) return;

    // Switch out row
    switchOutItem.rowType = SWITCH_ROW_TYPE.SWITCH_OUT;
    switchOutItem.position = groupIndex + 1;
    switchOutItem.configuration = { ...switchOutItem.configuration, highlighted: true };
    rows.push(switchOutItem);

    if (hideSwitchIns) return;

    // Switch in header row
    const switchOutItemId = getItemId ? getItemId(switchOutItem) : getAssetInternalId(switchOutItem.data);
    rows.push({
      switchOutItemId,
      rowType: SWITCH_ROW_TYPE.SWITCH_IN_HEADER,
    });

    // Switch in rows
    (switchOutItem.buy || []).forEach((buyItem) => {
      buyItem.rowType = SWITCH_ROW_TYPE.SWITCH_IN;
      buyItem.switchOutItemId = switchOutItemId;
      rows.push(buyItem);
    });
  });

  return rows;
};

// endregion
